import { useCallback, useEffect, useState } from 'react'
import { useSessao } from '../lib/sessao'
import { api, ErroApi } from '../lib/api'
import { Folha, Vazio, Carregando } from '../componentes/ui'
import './novo-registro.css'

/**
 * Escrever no prontuário.
 *
 * ─── POR QUE ASSINAR É O BOTÃO PRINCIPAL ───────────────────────────
 *
 * Rascunho que nunca é assinado é evolução que não existe: não vale em
 * auditoria, não sustenta glosa de convênio e não protege ninguém. No
 * sistema do concorrente salvar e assinar têm o mesmo peso visual, e o
 * resultado previsível é prontuário cheio de rascunho de dois anos
 * atrás. Aqui "Assinar" é o botão cheio e "Salvar rascunho" é o
 * discreto — quem quiser voltar depois pode, mas o caminho normal
 * termina o documento.
 *
 * ─── O QUE A ASSINATURA CARREGA ────────────────────────────────────
 *
 * Ao assinar, o banco COPIA para dentro da ficha o nome, o conselho, o
 * número e a UF de quem assinou (`app.ficha_carimbar`, migração 0026).
 * Não é um vínculo por id: se a fisioterapeuta corrigir o CREFITO
 * amanhã, esta ficha continua mostrando o registro de hoje. É isso que
 * faz o documento valer como prova.
 *
 * ─── AS MEDIÇÕES NÃO FICAM NO JSON ─────────────────────────────────
 *
 * Campo do tipo `escala` mapeado para um conceito canônico vira linha
 * em `observacao`, além de entrar nos dados da ficha. É o que permite
 * o gráfico de dor caindo de 8 para 3 existir sem escrever um parser
 * por formato de formulário.
 */

type Campo = {
  chave: string; rotulo: string; tipo: string
  obrigatorio?: boolean; min?: number; max?: number; opcoes?: string[]
  /* Passo da escala. Faltava, e o `step={1}` fixo tornava TUG (s),
     velocidade de marcha (m/s), Borg e goniometria (°) inregistráveis —
     o número que precisa de decimal não entrava. `unidade` rotula o
     valor ("s", "m/s", "°") para não virar número solto. */
  passo?: number; unidade?: string
}
type Bloco = { titulo: string; campos: Campo[] }
type Modelo = {
  id: string; nome: string; especie: string; versao: number
  esquema: { blocos?: Bloco[] }
  mapa_canonico?: Record<string, string>
}
type Prof = {
  id: string; nome: string; conselho: string
  conselho_numero: string | null; conselho_uf: string | null
  ativo: boolean
}

const ESPECIE: Record<string, string> = {
  AVALIACAO: 'Avaliação', EVOLUCAO: 'Evolução', ANAMNESE: 'Anamnese',
  ALTA: 'Alta', CONSENTIMENTO: 'Termo', LAUDO: 'Laudo',
}

export function NovoRegistro({ pacienteId, agendamentoId, aoFechar, aoSalvar }: {
  pacienteId: string
  agendamentoId?: string
  aoFechar: () => void
  aoSalvar: () => Promise<void>
}) {
  const { estado } = useSessao()
  /* Quem eu SOU como profissional nesta clínica, se for algum.
     Antes disto a tela oferecia um `<select>` com todos os
     profissionais e a pessoa se escolhia — o que deixava assinar com o
     CREFITO da colega, em documento IMUTÁVEL, oito vezes por dia. */
  const souProfissional = estado.fase === 'dentro' ? estado.ctx.profissionalId : null
  /* O carimbo sai completo? Perguntado ANTES, não na hora de assinar:
     ficha assinada é imutável, e descobrir no fim que o registro estava
     incompleto significa perder o que foi escrito ou salvar rascunho e
     esquecer. */
  const carimboOk = estado.fase === 'dentro' ? estado.ctx.podeAssinar : false

  const [modelos, setModelos] = useState<Modelo[]>([])
  const [profs, setProfs] = useState<Prof[]>([])
  const [modelo, setModelo] = useState<Modelo | null>(null)
  const [valores, setValores] = useState<Record<string, unknown>>({})
  const [quem, setQuem] = useState(souProfissional ?? '')
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([
        api<{ itens: Modelo[] }>('/v1/modelos'),
        api<{ itens: Prof[] }>('/v1/profissionais?limite=100'),
      ])
      setModelos(m.itens)
      const ativos = p.itens.filter((x) => x.ativo)
      setProfs(ativos)
      // Um profissional só: já vem escolhido. Clínica pequena é a
      // maioria do mercado, e um select de um item é atrito puro.
      if (ativos.length === 1) setQuem(ativos[0]!.id)
      if (m.itens.length === 1) setModelo(m.itens[0]!)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  function mudar(chave: string, v: unknown) {
    setValores((x) => ({ ...x, [chave]: v }))
  }

  function faltando(): string | null {
    for (const b of modelo?.esquema?.blocos ?? []) {
      for (const c of b.campos) {
        const v = valores[c.chave]
        if (c.obrigatorio && (v === undefined || v === '' || v === null)) {
          return `Falta preencher "${c.rotulo}".`
        }
      }
    }
    return null
  }

  async function enviar(assinar: boolean) {
    if (!modelo) return
    setErro(null)

    /* A validação de campo obrigatório vale SÓ no caminho de assinar.
       Rodá-la antes dos dois proibia salvar rascunho incompleto — que é
       o único estado em que rascunho serve para alguma coisa: a
       fisioterapeuta anota o que deu tempo entre dois atendimentos e
       completa depois. Ficha ASSINADA é imutável e aí sim a exigência
       vale, porque não há "depois". */
    if (assinar) {
      const falta = faltando()
      if (falta) { setErro(falta); return }
      if (!quem) { setErro('Diga quem está assinando.'); return }
    }

    // Campo de escala com conceito canônico vira também uma medição.
    // Sem isso o número fica preso no JSON e o gráfico não existe.
    const mapa = modelo.mapa_canonico ?? {}
    const observacoes = Object.entries(mapa)
      .filter(([campo]) => valores[campo] !== undefined && valores[campo] !== '')
      .map(([campo, chave]) => ({ chave, valor_num: Number(valores[campo]) }))
      .filter((o) => Number.isFinite(o.valor_num))

    setSalvando(true)
    try {
      await api('/v1/fichas', {
        metodo: 'POST',
        corpo: {
          paciente_id: pacienteId,
          modelo_id: modelo.id,
          dados: valores,
          assinar,
          ...(assinar ? { profissional_id: quem } : {}),
          ...(agendamentoId ? { agendamento_id: agendamentoId } : {}),
          ...(observacoes.length ? { observacoes } : {}),
        },
      })
      await aoSalvar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.')
      setSalvando(false)
    }
  }

  const escolhido = profs.find((p) => p.id === quem)

  return (
    <Folha titulo={modelo ? modelo.nome : 'Novo registro'}
           descricao={modelo
             ? `${ESPECIE[modelo.especie] ?? modelo.especie} · formulário versão ${modelo.versao}`
             : 'Escolha o que vai escrever.'}
           aoFechar={aoFechar}
           acoes={modelo && (
             <>
               <button type="button" className="botao botao--fantasma"
                       onClick={() => void enviar(false)} disabled={salvando}>
                 Salvar rascunho
               </button>
               <button type="button" className="botao"
                       onClick={() => void enviar(true)}
                       disabled={salvando || !quem || (!!souProfissional && !carimboOk)}
                       title={souProfissional && !carimboOk
                         ? 'Falta o seu conselho, número e UF'
                         : undefined}>
                 {salvando ? 'Gravando…' : 'Assinar e fechar'}
               </button>
             </>
           )}>
      {carregando ? <Carregando /> : (
        <div className="nr">
          {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

          {modelos.length === 0 ? (
            <Vazio
              mensagem="Nenhum formulário publicado."
              sugestao="Sem formulário não há onde escrever. Em Clínica › O prontuário dá para pegar um pronto em dois cliques."
            />
          ) : !modelo ? (
            <ul className="nr-escolha">
              {modelos.map((m) => (
                <li key={m.id}>
                  <button onClick={() => setModelo(m)}>
                    <strong>{m.nome}</strong>
                    <span className="ajuda">{ESPECIE[m.especie] ?? m.especie}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <form className="nr-form" onSubmit={(e) => e.preventDefault()}>
              {modelos.length > 1 && (
                <button type="button" className="nr-trocar"
                        onClick={() => { setModelo(null); setValores({}) }}>
                  ← trocar de formulário
                </button>
              )}

              {(modelo.esquema?.blocos ?? []).map((b, bi) => (
                <fieldset key={bi} className="nr-bloco">
                  <legend>{b.titulo}</legend>
                  {b.campos.map((c) => (
                    <CampoEntrada key={c.chave} c={c}
                                  valor={valores[c.chave]}
                                  aoMudar={(v) => mudar(c.chave, v)} />
                  ))}
                </fieldset>
              ))}

              <fieldset className="nr-bloco nr-assinatura">
                <legend>Quem assina</legend>
                {souProfissional && !carimboOk && (
                  <div className="aviso aviso--atencao" role="note">
                    Falta o seu registro profissional — <strong>conselho, número e UF</strong>.
                    Sem ele dá para salvar rascunho, mas não dá para assinar: o carimbo
                    é o que dá valor ao documento, e ficha assinada não se altera.
                    <br />
                    Cadastre em <strong>Ajustes › Quem trabalha aqui</strong>.
                  </div>
                )}

                {souProfissional ? (
                  /* Quem É profissional assina como si mesmo, sem
                     escolher. Oferecer a lista aqui seria oferecer a
                     chance de errar — e ficha assinada não se altera,
                     só se corrige por adendo. */
                  <p className="ajuda">
                    Você assina como <strong>{profs.find((p) => p.id === souProfissional)?.nome ?? 'você'}</strong>.
                  </p>
                ) : (
                  <div className="campo">
                    <label htmlFor="nr-quem">Profissional responsável</label>
                    <select id="nr-quem" className="entrada" value={quem}
                            onChange={(e) => setQuem(e.target.value)}>
                      <option value="">— escolha —</option>
                      {profs.map((p) => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                    <span className="ajuda">
                      Você não está cadastrado como profissional desta clínica,
                      então precisa dizer em nome de quem a evolução é assinada.
                    </span>
                  </div>
                )}

                {/* O carimbo é mostrado ANTES de assinar. Descobrir que
                    saiu sem registro depois de assinar é tarde: a ficha
                    assinada não se altera, só se corrige por adendo. */}
                {escolhido && (
                  <p className={`nr-carimbo ${
                    escolhido.conselho === 'NENHUM' ? 'is-sem' : ''}`}>
                    {escolhido.conselho === 'NENHUM' ? (
                      <>
                        Vai assinar como <strong>{escolhido.nome}</strong>, sem registro
                        de conselho. Se esta pessoa tem conselho, cadastre o número
                        antes — depois de assinar a ficha não muda mais.
                      </>
                    ) : (
                      <>
                        Vai constar na ficha:{' '}
                        <strong>
                          {escolhido.nome} · {escolhido.conselho}-{escolhido.conselho_uf}{' '}
                          {escolhido.conselho_numero}
                        </strong>
                      </>
                    )}
                  </p>
                )}
              </fieldset>
            </form>
          )}
        </div>
      )}
    </Folha>
  )
}

/* ══ um campo ══════════════════════════════════════════════════════ */

function CampoEntrada({ c, valor, aoMudar }: {
  c: Campo; valor: unknown; aoMudar: (v: unknown) => void
}) {
  const id = `nr-${c.chave}`
  const v = valor ?? ''

  if (c.tipo === 'escala') {
    const min = c.min ?? 0
    const max = c.max ?? 10
    const passo = c.passo ?? 1
    // Só faz sentido arrastar quando a faixa é curta e inteira (EVA
    // 0–10). Uma medida contínua (TUG 0–60 s) num slider é grossa
    // demais; aí o número exato é o que serve.
    const comSlider = passo >= 1 && max - min <= 20
    return (
      <div className="campo nr-escala">
        <label htmlFor={id}>
          {c.rotulo}{c.obrigatorio && <span aria-hidden="true"> *</span>}
        </label>
        <div className="nr-escala__corpo">
          {comSlider && (
            <input type="range" min={min} max={max} step={passo}
                   value={v === '' ? min : Number(v)}
                   aria-label={c.rotulo}
                   onChange={(e) => aoMudar(Number(e.target.value))} />
          )}
          {/* O número é exato e aceita o decimal que o range não dá. É
              ele o campo que carrega o id/foco. */}
          <input id={id} type="number" className="entrada nr-escala__num"
                 min={min} max={max} step={passo} inputMode="decimal"
                 value={v === '' ? '' : String(v)}
                 onChange={(e) => aoMudar(e.target.value === '' ? '' : Number(e.target.value))} />
          {c.unidade && <span className="nr-escala__un">{c.unidade}</span>}
        </div>
        <span className="ajuda">{min} a {max}{c.unidade ? ` ${c.unidade}` : ''}</span>
      </div>
    )
  }

  if (c.tipo === 'selecao') {
    return (
      <div className="campo">
        <label htmlFor={id}>
          {c.rotulo}{c.obrigatorio && <span aria-hidden="true"> *</span>}
        </label>
        <select id={id} className="entrada" value={String(v)}
                onChange={(e) => aoMudar(e.target.value)}>
          <option value="">— escolha —</option>
          {(c.opcoes ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    )
  }

  if (c.tipo === 'booleano') {
    return (
      <label className="nr-check">
        <input type="checkbox" checked={v === true}
               onChange={(e) => aoMudar(e.target.checked)} />
        <span>{c.rotulo}</span>
      </label>
    )
  }

  if (c.tipo === 'texto_longo') {
    return (
      <div className="campo">
        <label htmlFor={id}>
          {c.rotulo}{c.obrigatorio && <span aria-hidden="true"> *</span>}
        </label>
        <textarea id={id} className="entrada" rows={3} maxLength={4000}
                  value={String(v)} onChange={(e) => aoMudar(e.target.value)} />
      </div>
    )
  }

  return (
    <div className="campo">
      <label htmlFor={id}>
        {c.rotulo}{c.obrigatorio && <span aria-hidden="true"> *</span>}
      </label>
      <input id={id} className={`entrada ${c.tipo === 'numero' ? 'mono' : ''}`}
             type={c.tipo === 'numero' ? 'number' : c.tipo === 'data' ? 'date' : 'text'}
             maxLength={c.tipo === 'texto' ? 300 : undefined}
             value={String(v)}
             onChange={(e) => aoMudar(
               c.tipo === 'numero'
                 ? (e.target.value === '' ? '' : Number(e.target.value))
                 : e.target.value)} />
    </div>
  )
}
